/* Admin Accounting panel (iter-21).
 *
 * Visualises the backend `/admin/accounting/summary` payload:
 *   - revenue / COGS / gross-profit / OPEX / net-profit
 *   - day / week / month granularity toggle
 *   - date range picker (default: last 30 days)
 *   - stacked bar chart: COGS + OPEX vs Gross Profit
 *   - line chart: Net Profit trend
 *   - top-line totals cards
 *   - inline OPEX editor (record / list / delete)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { adminApi, editorialOrdersAdminApi } from '@/lib/api';
import { bookstoreAdminApi } from '@/lib/bookstoreApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DollarSign, TrendingUp, TrendingDown, Receipt, Loader2, Plus, Trash2, ShieldCheck, ShieldAlert, RefreshCw, Wallet, Clock, Send, Zap, AlertTriangle, Mail, UserPlus, X, Users, Shield, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader, Field } from './_shared';
import VendorInvoicesSection from './VendorInvoicesSection';
import CopyrightFilingDashCard from './CopyrightFilingDashCard';
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/api';
import { openConfirm, openPrompt } from '@/components/admin/ConfirmDialog';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

function fmtUsd(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (Math.abs(n) < 1) return `$${n.toFixed(4)}`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function isoNDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 19) + 'Z';
}

function TotalCard({ icon: Icon, label, value, tone, testid }) {
  const color =
    tone === 'positive' ? 'text-success' :
    tone === 'negative' ? 'text-destructive' :
    tone === 'primary'  ? 'text-primary'   : 'text-foreground';
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={cn('mt-1 font-mono-stat text-[18px] font-semibold', color)}>
        {value}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Bookstore Revenue Card — live sales from book_sales_ledger
// ───────────────────────────────────────────────────────────────────────────
function BookstoreRevenueCard() {
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [days, setDays]   = useState(30);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await bookstoreAdminApi.salesSummary(days);
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bookstore sales.');
    } finally { setBusy(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const fmt = (cents) => cents != null ? `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

  const totals = data?.totals || {};
  const recent = data?.recent_sales || [];

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="bs-revenue-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-amber-600" />
          <span className="text-[13px] font-semibold">Bookstore Revenue</span>
          <span className="text-[11px] text-muted-foreground">(85% Author / 15% Platform)</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-[11px] rounded border border-border bg-background px-2 py-1"
            data-testid="bs-revenue-days-select"
          >
            {[7, 30, 90, 365].map((d) => (
              <option key={d} value={d}>Last {d} days</option>
            ))}
          </select>
          <button onClick={load} disabled={busy}
                  className="rounded border border-border p-1 hover:bg-secondary"
                  data-testid="bs-revenue-refresh-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total Sales',       value: totals.total_sales?.toLocaleString() ?? '0',         sub: 'transactions',           testid: 'bs-rev-total-sales' },
          { label: 'Gross Revenue',     value: fmt(totals.total_gross_cents),                        sub: 'incl. author royalties',  testid: 'bs-rev-gross' },
          { label: 'Platform Revenue',  value: fmt(totals.total_platform_cents),                     sub: 'Crown & Rose 15%',        testid: 'bs-rev-platform' },
          { label: 'Author Royalties',  value: fmt(totals.total_author_cents),                       sub: 'payable to authors',      testid: 'bs-rev-author' },
        ].map(({ label, value, sub, testid }) => (
          <div key={label} className="rounded-md border border-border bg-secondary/30 px-3 py-2" data-testid={testid}>
            <p className="text-[10.5px] text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-[17px] font-bold font-mono text-foreground">{value}</p>
            <p className="text-[9.5px] text-muted-foreground/70">{sub}</p>
          </div>
        ))}
      </div>

      {/* Recent sales table */}
      {recent.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11.5px]" data-testid="bs-revenue-recent-table">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="pb-1.5 pr-3">Book</th>
                <th className="pb-1.5 pr-3">Author</th>
                <th className="pb-1.5 pr-3 text-right">Gross</th>
                <th className="pb-1.5 pr-3 text-right">Platform (15%)</th>
                <th className="pb-1.5 text-right">Author (85%)</th>
                <th className="pb-1.5 pl-3 text-right">Date</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((s, i) => (
                <tr key={i} className="border-b border-border/40" data-testid={`bs-revenue-row-${i}`}>
                  <td className="py-1 pr-3 font-medium truncate max-w-[140px]">{s.title || s.book_id}</td>
                  <td className="py-1 pr-3 text-muted-foreground truncate max-w-[100px]">{s.author || '—'}</td>
                  <td className="py-1 pr-3 text-right font-mono">{fmt(s.gross_cents)}</td>
                  <td className="py-1 pr-3 text-right font-mono text-emerald-700">{fmt(s.platform_share_cents)}</td>
                  <td className="py-1 text-right font-mono text-amber-700">{fmt(s.author_share_cents)}</td>
                  <td className="py-1 pl-3 text-right text-muted-foreground">{(s.sold_at || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {recent.length === 0 && !busy && (
        <p className="text-[11.5px] text-muted-foreground italic">No sales in the last {days} days.</p>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Bookstore Inventory Card — published catalog with catalog value
// ───────────────────────────────────────────────────────────────────────────
function BookstoreInventoryCard() {
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { setData(await bookstoreAdminApi.publishedInventory()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load inventory.'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const books = (data?.books || []).filter((b) =>
    !search || (b.title + b.author + b.genre_slug).toLowerCase().includes(search.toLowerCase())
  );
  const visible = expanded ? books : books.slice(0, 8);

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="bs-inventory-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-emerald-600" />
          <span className="text-[13px] font-semibold">Bookstore Inventory</span>
          {data && <span className="text-[11px] text-muted-foreground">({data.total_books} published titles)</span>}
        </div>
        <button onClick={load} disabled={busy} className="rounded border border-border p-1 hover:bg-secondary" data-testid="bs-inventory-refresh">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Summary banner */}
      {data && (
        <div className="flex items-center gap-4 rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-2">
          <div>
            <p className="text-[10.5px] uppercase tracking-wider text-emerald-700">Total Published Titles</p>
            <p className="text-[20px] font-bold font-mono text-emerald-900" data-testid="bs-inv-total-books">{data.total_books}</p>
          </div>
          <div className="h-8 w-px bg-emerald-200" />
          <div>
            <p className="text-[10.5px] uppercase tracking-wider text-emerald-700">Gross Catalog Value</p>
            <p className="text-[20px] font-bold font-mono text-emerald-900" data-testid="bs-inv-catalog-value">{fmt(data.total_catalog_value_cents)}</p>
          </div>
          <div className="h-8 w-px bg-emerald-200" />
          <div>
            <p className="text-[10.5px] uppercase tracking-wider text-emerald-700">Platform Revenue (15%)</p>
            <p className="text-[20px] font-bold font-mono text-emerald-900">{fmt(Math.round((data.total_catalog_value_cents || 0) * 0.15))}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by title or author…"
             className="w-full rounded border border-border bg-background px-3 py-1.5 text-[12px] focus:outline-none focus:border-primary"
             data-testid="bs-inv-search" />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11.5px]" data-testid="bs-inv-table">
          <thead>
            <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground text-left">
              <th className="pb-1.5 pr-3">Title</th>
              <th className="pb-1.5 pr-3">Author</th>
              <th className="pb-1.5 pr-3">Genre</th>
              <th className="pb-1.5 text-right">List Price</th>
              <th className="pb-1.5 pl-3 text-right">Platform (15%)</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((b, i) => (
              <tr key={b.id || i} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`bs-inv-row-${i}`}>
                <td className="py-1 pr-3 font-medium max-w-[200px] truncate">{b.title}</td>
                <td className="py-1 pr-3 text-muted-foreground max-w-[120px] truncate">{b.author || '—'}</td>
                <td className="py-1 pr-3 text-muted-foreground">{(b.genre_slug || '').replace(/-/g, ' ')}</td>
                <td className="py-1 text-right font-mono">{fmt(b.price_cents)}</td>
                <td className="py-1 pl-3 text-right font-mono text-emerald-700">{fmt(Math.round((b.price_cents || 0) * 0.15))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {books.length > 8 && (
        <button onClick={() => setExpanded((v) => !v)} className="text-[11.5px] text-primary hover:underline" data-testid="bs-inv-show-more">
          {expanded ? 'Show less' : `Show all ${books.length} titles`}
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Revenue Stream Margin Summary — static business model card
// ───────────────────────────────────────────────────────────────────────────
const MARGIN_STREAMS = [
  { stream: 'Subscriptions (Scribe–Studio)',     margin: '71–81%',       note: 'Gross margin after LLM + CS + Stripe', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  { stream: 'BYOK Subscriptions',               margin: '81–96%',       note: 'No platform LLM cost; Stripe + CS only', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  { stream: 'Human Editorial (20% take)',       margin: '~100%',        note: 'Pure platform fee; editor keeps 80%', color: 'text-blue-700', bg: 'bg-blue-50' },
  { stream: 'Audiobook Production (40500)',     margin: '~50–80%',      note: 'Depending on TTS provider & volume', color: 'text-sky-700', bg: 'bg-sky-50' },
  { stream: 'Narration Standard (40600)',       margin: '~75%',         note: '$9.99/book · Flash v2.5 · ~$0.04/1K chars', color: 'text-violet-700', bg: 'bg-violet-50' },
  { stream: 'Narration Studio (40600)',         margin: '~47%',         note: '$24.99/book · Multilingual v2 · ~$0.08/1K chars', color: 'text-violet-700', bg: 'bg-violet-50' },
  { stream: 'Audiobook Sales (40700)',          margin: 'Near-zero COGS', note: 'Reader purchases narrated titles; cost already amortised', color: 'text-amber-700', bg: 'bg-amber-50' },
  { stream: 'Manuscript Acquisition (15100)',   margin: '—',            note: 'Balance-sheet asset; COGS recognised at publish (51800)', color: 'text-rose-700', bg: 'bg-rose-50' },
  { stream: 'AI Token Top-ups',                 margin: '~60–70%',      note: 'Starter $10 · Pro $40 · Power $120', color: 'text-violet-700', bg: 'bg-violet-50' },
  { stream: 'Copyscape Scan Packs',             margin: '~75%',         note: '10 → $5 · 50 → $20 · 120 → $40', color: 'text-indigo-700', bg: 'bg-indigo-50' },
  { stream: 'Bookstore (15% take)',             margin: 'Near-zero COGS', note: 'Authors supply content; 85% routes to them', color: 'text-amber-700', bg: 'bg-amber-50' },
];

function RevenueStreamMarginCard() {
  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="revenue-margin-card">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-emerald-600" />
        <span className="text-[13px] font-semibold">Revenue Stream Margin Summary</span>
        <span className="text-[11px] text-muted-foreground">— business model snapshot</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11.5px]" data-testid="revenue-margin-table">
          <thead>
            <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground text-left">
              <th className="pb-1.5 pr-4">Stream</th>
              <th className="pb-1.5 pr-4">Gross Margin</th>
              <th className="pb-1.5">Notes</th>
            </tr>
          </thead>
          <tbody>
            {MARGIN_STREAMS.map((row) => (
              <tr key={row.stream} className="border-b border-border/40">
                <td className="py-1.5 pr-4 font-medium">{row.stream}</td>
                <td className="py-1.5 pr-4">
                  <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${row.bg} ${row.color}`} data-testid={`margin-badge-${row.stream.split(' ')[0].toLowerCase()}`}>
                    {row.margin}
                  </span>
                </td>
                <td className="py-1.5 text-muted-foreground">{row.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10.5px] text-muted-foreground italic">
        Subscription revenue uses blended LLM cost of ~$4.10/M tokens. Margins assume standard Stripe fee (2.9% + $0.30) and $15/mo customer-success allocation.
      </p>
    </div>
  );
}



export default function AccountingPanel() {
  const [granularity, setGranularity] = useState('day');
  const [rangeDays, setRangeDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [opex, setOpex] = useState([]);
  const [busy, setBusy] = useState(false);
  // OPEX form
  const [opexMonth, setOpexMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [opexAmount, setOpexAmount] = useState('');
  const [opexCategory, setOpexCategory] = useState('general');
  const [opexNote, setOpexNote] = useState('');

  const reload = async () => {
    setBusy(true);
    try {
      const [s, o] = await Promise.all([
        adminApi.accountingSummary({
          from_ts: isoNDaysAgo(rangeDays),
          to_ts: new Date().toISOString(),
          granularity,
        }),
        adminApi.listOpex().catch(() => ({ opex: [] })),
      ]);
      setSummary(s);
      setOpex(o.opex || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load accounting data.');
    } finally { setBusy(false); }
  };
  // iter55f58c — `reload` is a stable arrow; deps intentionally scoped
  // to the query parameters we react to.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [granularity, rangeDays]);

  const addOpex = async () => {
    const amt = parseFloat(opexAmount);
    if (!opexMonth.match(/^\d{4}-\d{2}$/)) { toast.error('Month must be YYYY-MM.'); return; }
    if (!(amt > 0)) { toast.error('Amount must be > 0.'); return; }
    try {
      await adminApi.recordOpex({
        month: opexMonth, amount_usd: amt,
        category: opexCategory || 'general', note: opexNote || '',
      });
      toast.success('OPEX recorded.');
      setOpexAmount(''); setOpexNote('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'OPEX failed.');
    }
  };

  const removeOpex = async (id) => {
    if (!(await openConfirm({
      title: 'Delete this OPEX entry?',
      description: 'The row will be removed from the OPEX ledger. This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'destructive',
    }))) return;
    try {
      await adminApi.deleteOpex(id);
      toast.success('OPEX removed.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const chartData = useMemo(() => {
    if (!summary) return [];
    return summary.buckets.map((b) => ({
      bucket: b.bucket,
      Revenue: b.revenue_usd,
      COGS: b.cogs_usd,
      'Gross Profit': b.gross_profit_usd,
      OPEX: b.opex_usd,
      'Net Profit': b.net_profit_usd,
    }));
  }, [summary]);

  if (!summary) {
    return (
      <section>
        <PanelHeader title="Accounting" />
        <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      </section>
    );
  }

  const t = summary.totals;
  const margin = t.revenue_usd > 0 ? Math.round((t.gross_profit_usd / t.revenue_usd) * 100) : 0;

  return (
    <section data-testid="admin-accounting-panel">
      <PanelHeader title="Accounting" />
      <p className="mb-4 text-[11.5px] text-muted-foreground">
        Revenue, COGS, Gross Profit and Net Profit derived from the <code>cost_events</code> ledger
        joined with admin-entered OPEX. Use the granularity / range toggles to view the same
        data daily, weekly, or monthly.
      </p>

      {/* Controls */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5 text-[11px]" data-testid="acc-granularity">
          {['day', 'week', 'month'].map((g) => (
            <button key={g} onClick={() => setGranularity(g)}
                    data-testid={`acc-granularity-${g}`}
                    className={cn('rounded-sm px-2 py-0.5 capitalize',
                      granularity === g ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}>
              {g}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5 text-[11px]" data-testid="acc-range">
          {[7, 30, 90, 365].map((d) => (
            <button key={d} onClick={() => setRangeDays(d)}
                    data-testid={`acc-range-${d}`}
                    className={cn('rounded-sm px-2 py-0.5',
                      rangeDays === d ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}>
              {d}d
            </button>
          ))}
        </div>
        {busy && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>

      {/* Totals */}
      <div className="mb-5 grid grid-cols-2 gap-2 md:grid-cols-5">
        <TotalCard icon={DollarSign} label="Revenue" tone="primary" testid="acc-total-revenue"
                    value={fmtUsd(t.revenue_usd)} />
        <TotalCard icon={Receipt}   label="COGS"    tone="negative" testid="acc-total-cogs"
                    value={fmtUsd(t.cogs_usd)} />
        <TotalCard icon={TrendingUp} label={`Gross Profit · ${margin}%`} tone="positive" testid="acc-total-gp"
                    value={fmtUsd(t.gross_profit_usd)} />
        <TotalCard icon={Receipt}   label="OPEX"    tone="negative" testid="acc-total-opex"
                    value={fmtUsd(t.opex_usd)} />
        <TotalCard icon={TrendingUp} label="Net Profit"
                    tone={t.net_profit_usd >= 0 ? 'positive' : 'negative'} testid="acc-total-net"
                    value={fmtUsd(t.net_profit_usd)} />
      </div>

      {/* Stacked bar chart */}
      <div className="mb-5 rounded-md border border-border bg-card p-3" data-testid="acc-chart-bars">
        <div className="mb-2 text-[12.5px] font-semibold">Revenue vs COGS &amp; OPEX</div>
        <div style={{ width: '100%', height: 240 }}>
          {chartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-[11.5px] text-muted-foreground">
              No events in this range.
            </div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => `$${v.toFixed(0)}`} />
                <Tooltip
                  contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }}
                  formatter={(v) => fmtUsd(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Revenue"      fill="hsl(var(--primary))"      radius={[2,2,0,0]} />
                <Bar dataKey="COGS"          fill="hsl(var(--destructive))" radius={[2,2,0,0]} />
                <Bar dataKey="OPEX"          fill="hsl(var(--warning))"     radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Net Profit trend */}
      <div className="mb-5 rounded-md border border-border bg-card p-3" data-testid="acc-chart-net">
        <div className="mb-2 text-[12.5px] font-semibold">Net Profit trend</div>
        <div style={{ width: '100%', height: 180 }}>
          {chartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-[11.5px] text-muted-foreground">
              No events in this range.
            </div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => `$${v.toFixed(0)}`} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }}
                          formatter={(v) => fmtUsd(v)} />
                <Line dataKey="Gross Profit" stroke="hsl(var(--success))" strokeWidth={2} dot={false} />
                <Line dataKey="Net Profit"   stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Feb-2026 iter — Cash Flow + Accounts Audit sections.
          Read-only reconciliation surface for the CFO. */}
      <CashFlowSection />
      <AccountsAuditSection />
      <LedgerSection />
      <AgedReportsSection />
      <VendorInvoicesSection />
      <CopyrightFilingDashCard />
      <BookstoreRevenueCard />
      <BookstoreInventoryCard />
      <RevenueStreamMarginCard />
      <ReconciliationSection />
      <EditorialPayoutsSection />
      <DigestSubscribersSection />

      {/* OPEX editor */}
      <div className="rounded-md border border-border bg-card p-4" data-testid="acc-opex-card">
        <div className="mb-1 text-[12.5px] font-semibold">Operating Expenses (OPEX)</div>
        <p className="mb-3 text-[11px] text-muted-foreground">
          Record monthly business overhead (hosting, salaries, marketing). OPEX is apportioned
          evenly across day/week buckets when computing Net Profit.
        </p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
          <Field label="Month (YYYY-MM)">
            <Input value={opexMonth} onChange={(e) => setOpexMonth(e.target.value)}
                    className="h-8 font-mono-stat text-[12px]"
                    placeholder="2026-02" data-testid="acc-opex-month" />
          </Field>
          <Field label="Amount (USD)">
            <Input type="number" min={0} step="0.01" value={opexAmount}
                    onChange={(e) => setOpexAmount(e.target.value)}
                    className="h-8 font-mono-stat text-[12px]" data-testid="acc-opex-amount" />
          </Field>
          <Field label="Category">
            <Input value={opexCategory} onChange={(e) => setOpexCategory(e.target.value)}
                    className="h-8 text-[12px]" data-testid="acc-opex-category" />
          </Field>
          <Field label="Note (optional)" className="md:col-span-2">
            <Input value={opexNote} onChange={(e) => setOpexNote(e.target.value)}
                    className="h-8 text-[12px]" data-testid="acc-opex-note" />
          </Field>
        </div>
        <div className="mt-3">
          <Button size="sm" onClick={addOpex} className="gap-1.5" data-testid="acc-opex-add">
            <Plus className="h-3.5 w-3.5" /> Add OPEX
          </Button>
        </div>
        {opex.length > 0 ? (
          <table className="mt-4 w-full text-[11.5px]" data-testid="acc-opex-table">
            <thead className="bg-panel-header/60 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Month</th>
                <th className="px-3 py-2 text-left font-medium">Category</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 text-left font-medium">Note</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {opex.map((o) => (
                <tr key={o.id} className="border-t border-border" data-testid={`acc-opex-row-${o.id}`}>
                  <td className="px-3 py-1.5 font-mono-stat">{o.month}</td>
                  <td className="px-3 py-1.5">{o.category}</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{fmtUsd(o.amount_usd)}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{o.note || '—'}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => removeOpex(o.id)}
                             data-testid={`acc-opex-delete-${o.id}`}
                             className="text-destructive hover:underline">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="mt-4 rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-[11.5px] text-muted-foreground" data-testid="acc-opex-table">
            No OPEX rows yet. Record one above to start tracking overhead alongside revenue.
          </div>
        )}
      </div>
    </section>
  );
}

// ────────────────────────── Cash Flow Analysis ──────────────────────────

function CashFlowSection() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setData(await adminApi.cashFlow(months)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cash-flow load failed.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [months]);
  const series = data?.series || [];
  const t = data?.totals || {};
  const b = data?.burn || {};
  return (
    <div className="mb-5 rounded-md border border-border bg-card p-4" data-testid="acc-cash-flow">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <Wallet className="h-4 w-4" /> Cash Flow
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Monthly Stripe inflow vs COGS + OPEX outflow. Running balance
            is the cumulative sum. Burn = trailing-3 avg outflow − inflow.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5 text-[11px]" data-testid="acc-cash-months">
            {[3, 6, 12].map((m) => (
              <button key={m} onClick={() => setMonths(m)}
                data-testid={`acc-cash-months-${m}`}
                className={cn('rounded-sm px-2 py-0.5',
                  months === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}>
                {m}m
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
            className="h-7 gap-1 text-[11px]" data-testid="acc-cash-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <TotalCard icon={TrendingUp} label="Inflow" tone="positive" testid="acc-cash-inflow"
          value={fmtUsd(t.inflow_usd)} />
        <TotalCard icon={TrendingDown} label="Outflow" tone="negative" testid="acc-cash-outflow"
          value={fmtUsd(t.outflow_usd)} />
        <TotalCard icon={Wallet} label="Ending Balance"
          tone={(t.ending_balance_usd ?? 0) >= 0 ? 'positive' : 'negative'}
          testid="acc-cash-ending"
          value={fmtUsd(t.ending_balance_usd)} />
        <TotalCard icon={TrendingDown} label={`Burn · ${b.runway_months != null ? `${b.runway_months}mo runway` : 'no burn'}`}
          tone={b.monthly_burn_usd > 0 ? 'negative' : 'positive'} testid="acc-cash-burn"
          value={fmtUsd(b.monthly_burn_usd)} />
      </div>

      <div className="mt-3" style={{ width: '100%', height: 200 }} data-testid="acc-cash-chart">
        {series.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[11.5px] text-muted-foreground">
            No cash movement in this window.
          </div>
        ) : (
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v) => `$${v.toFixed(0)}`} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }}
                formatter={(v) => fmtUsd(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area dataKey="inflow_usd"          name="Inflow"  stroke="hsl(var(--success))"     fill="hsl(var(--success))"     fillOpacity={0.25} />
              <Area dataKey="outflow_usd"         name="Outflow" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive))" fillOpacity={0.25} />
              <Line dataKey="running_balance_usd" name="Balance" stroke="hsl(var(--primary))"     strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-3 max-h-56 overflow-auto rounded border border-border">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-panel-header/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left">Month</th>
              <th className="px-2 py-1.5 text-right">Inflow</th>
              <th className="px-2 py-1.5 text-right">COGS</th>
              <th className="px-2 py-1.5 text-right">OPEX</th>
              <th className="px-2 py-1.5 text-right">Net</th>
              <th className="px-2 py-1.5 text-right">Running</th>
              <th className="px-2 py-1.5 text-right">Txns</th>
            </tr>
          </thead>
          <tbody>
            {series.map((r) => (
              <tr key={r.month} className="border-t border-border" data-testid={`acc-cash-row-${r.month}`}>
                <td className="px-2 py-1 font-mono-stat">{r.month}</td>
                <td className="px-2 py-1 text-right font-mono-stat text-success">{fmtUsd(r.inflow_usd)}</td>
                <td className="px-2 py-1 text-right font-mono-stat">{fmtUsd(r.cogs_usd)}</td>
                <td className="px-2 py-1 text-right font-mono-stat">{fmtUsd(r.opex_usd)}</td>
                <td className={cn('px-2 py-1 text-right font-mono-stat', r.net_cash_usd >= 0 ? 'text-success' : 'text-destructive')}>
                  {fmtUsd(r.net_cash_usd)}
                </td>
                <td className="px-2 py-1 text-right font-mono-stat font-semibold">{fmtUsd(r.running_balance_usd)}</td>
                <td className="px-2 py-1 text-right font-mono-stat text-muted-foreground">{r.transactions || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────── Accounts Audit ──────────────────────────

function AccountsAuditSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setData(await adminApi.accountsAudit()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Audit load failed.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const findings = data?.findings || [];
  const score = data?.score ?? 0;
  const gradeTone = data?.grade === 'healthy' ? 'bg-emerald-500'
                    : data?.grade === 'watch' ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="mb-5 rounded-md border border-border bg-card p-4" data-testid="acc-audit">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            {score >= 90 ? <ShieldCheck className="h-4 w-4 text-success" /> : <ShieldAlert className="h-4 w-4 text-amber-500" />}
            Accounts Audit
            <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white', gradeTone)}
              data-testid="acc-audit-grade">
              {data?.grade || '—'} · {score}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Reconciliation checks against Stripe, cost events, and OPEX. Each finding lists
            the recommended action.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="acc-audit-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Re-run
        </Button>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {findings.map((f) => (
          <div key={f.id} className="rounded border border-border bg-background/70 p-3"
            data-testid={`acc-audit-${f.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-semibold">{f.label}</span>
              <span className={cn('rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider',
                f.severity === 'high' ? 'bg-rose-500/15 text-rose-600'
                : f.severity === 'medium' ? 'bg-amber-500/15 text-amber-600'
                : 'bg-emerald-500/15 text-emerald-600')}>
                {f.severity}
              </span>
            </div>
            <div className="mt-1 font-mono-stat text-[13px]">
              {f.value_pct != null
                ? <>{f.count} / {f.denominator} <span className="text-[10.5px] text-muted-foreground">({f.value_pct}%)</span></>
                : f.count}
              {f.months_missing?.length ? (
                <span className="ml-2 text-[10.5px] text-muted-foreground">
                  ({f.months_missing.join(', ')})
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-[10.5px] text-muted-foreground">{f.recommendation}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────── CFO Ledger (Feb-2026) ──────────────────────────
// Staff-input spreadsheet: any admin with `financials` or `pro_writer`
// scope can insert cost rows (vendor invoices, freelancer bills) or
// AR rows (money owed to us). The CFO sees the full list, filters by
// kind + status + date, and marks rows cleared inline.

function LedgerSection() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filterKind, setFilterKind] = useState('all');      // all|cost|ar
  const [filterStatus, setFilterStatus] = useState('all');  // all|pending|cleared|cancelled
  const [rangeDays, setRangeDaysL] = useState(90);

  const [form, setForm] = useState({
    kind: 'cost', party: '', category: 'general',
    amount_usd: '', occurred_on: new Date().toISOString().slice(0,10), notes: '',
  });

  const load = async () => {
    setBusy(true);
    try {
      const since = new Date(); since.setUTCDate(since.getUTCDate() - rangeDays);
      const params = {
        status: filterStatus,
        date_from: since.toISOString().slice(0,10),
        limit: 500,
      };
      if (filterKind !== 'all') params.kind = filterKind;
      const [l, s] = await Promise.all([
        adminApi.ledgerList(params),
        adminApi.ledgerSummary(Math.min(24, Math.max(1, Math.ceil(rangeDays / 30)))),
      ]);
      setRows(l.entries || []);
      setTotals(l.totals || {});
      setSummary(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ledger load failed.');
    } finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterKind, filterStatus, rangeDays]);

  const addRow = async () => {
    const amt = parseFloat(form.amount_usd);
    if (!form.party.trim()) { toast.error(form.kind === 'cost' ? 'Vendor is required.' : 'Customer is required.'); return; }
    if (!(amt > 0)) { toast.error('Amount must be > 0.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.occurred_on)) { toast.error('Date must be YYYY-MM-DD.'); return; }
    try {
      await adminApi.ledgerCreate({
        kind: form.kind,
        party: form.party.trim(),
        category: form.category || 'general',
        amount_usd: amt,
        currency: 'USD',
        occurred_on: form.occurred_on,
        notes: form.notes || '',
      });
      toast.success(form.kind === 'cost' ? 'Cost recorded.' : 'AR row recorded.');
      setForm({ ...form, party: '', amount_usd: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ledger insert failed.');
    }
  };

  const setStatus = async (id, status) => {
    try {
      await adminApi.ledgerStatus(id, status);
      toast.success(`Row ${status}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };

  const removeRow = async (id) => {
    const reason = await openPrompt({
      title: 'Void ledger row',
      description:
        'GAAP-compliant void — the original row stays; a linked reversal row is appended. ' +
        'Enter a reason (min 3 characters):',
      placeholder: 'e.g. duplicate entry / wrong vendor / typo in amount',
      confirmLabel: 'Void row',
      minLength: 3,
    });
    if (reason === null) return;      // Cancel
    try {
      const r = await adminApi.ledgerVoid(id, reason);
      toast.success(`Row voided (reversal ${r.reversal?.slice(0,10)}…).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Void failed.');
    }
  };

  const net = (totals.ar_cleared || 0) - (totals.cost_cleared || 0);

  return (
    <div className="mb-5 rounded-md border border-border bg-card p-4" data-testid="acc-ledger">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <Receipt className="h-4 w-4" /> CFO Ledger
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Staff-entered cost &amp; accounts-receivable rows. Filter by kind or status;
            mark rows cleared or cancelled inline. Any admin with the
            <code className="mx-1">financials</code> or
            <code className="mx-1">pro_writer</code> scope can add rows.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="acc-ledger-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </Button>
      </div>

      {/* Filter strip */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px]">
        <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5" data-testid="acc-ledger-kind">
          {[['all','All'],['cost','Costs'],['ar','A/R']].map(([k, label]) => (
            <button key={k} onClick={() => setFilterKind(k)}
              data-testid={`acc-ledger-kind-${k}`}
              className={cn('rounded-sm px-2 py-0.5',
                filterKind === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5" data-testid="acc-ledger-status">
          {['all','pending','cleared','cancelled'].map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              data-testid={`acc-ledger-status-${s}`}
              className={cn('rounded-sm px-2 py-0.5 capitalize',
                filterStatus === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5" data-testid="acc-ledger-range">
          {[30, 90, 180, 365].map((d) => (
            <button key={d} onClick={() => setRangeDaysL(d)}
              data-testid={`acc-ledger-range-${d}`}
              className={cn('rounded-sm px-2 py-0.5',
                rangeDays === d ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Totals strip */}
      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-5">
        <TotalCard icon={Receipt}    label="Cost · pending"  tone="negative" testid="acc-ledger-total-cost-pending"
          value={fmtUsd(totals.cost_pending)} />
        <TotalCard icon={TrendingDown} label="Cost · cleared" tone="negative" testid="acc-ledger-total-cost-cleared"
          value={fmtUsd(totals.cost_cleared)} />
        <TotalCard icon={DollarSign} label="A/R · pending"   tone="primary"  testid="acc-ledger-total-ar-pending"
          value={fmtUsd(totals.ar_pending)} />
        <TotalCard icon={TrendingUp} label="A/R · cleared"   tone="positive" testid="acc-ledger-total-ar-cleared"
          value={fmtUsd(totals.ar_cleared)} />
        <TotalCard icon={TrendingUp} label="Net · cleared"
          tone={net >= 0 ? 'positive' : 'negative'} testid="acc-ledger-total-net"
          value={fmtUsd(net)} />
      </div>

      {/* Monthly rollup chart */}
      {summary?.series?.length ? (
        <div className="mb-4 rounded border border-border bg-background/50 p-3" data-testid="acc-ledger-chart">
          <div className="mb-2 text-[11.5px] font-semibold">Monthly rollup</div>
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer>
              <BarChart data={summary.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) => `$${v.toFixed(0)}`} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }}
                  formatter={(v) => fmtUsd(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ar_usd"   name="A/R"   fill="hsl(var(--success))"     radius={[2,2,0,0]} />
                <Bar dataKey="cost_usd" name="Costs" fill="hsl(var(--destructive))" radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      {/* Insert form */}
      <div className="mb-3 rounded border border-dashed border-border bg-background/40 p-3" data-testid="acc-ledger-form">
        <div className="mb-2 text-[11.5px] font-semibold">Add row</div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
          <Field label="Kind">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="h-8 w-full rounded-md border border-border bg-background px-2 text-[12px]"
              data-testid="acc-ledger-form-kind">
              <option value="cost">Cost</option>
              <option value="ar">A/R</option>
            </select>
          </Field>
          <Field label={form.kind === 'cost' ? 'Vendor' : 'Customer'} className="md:col-span-2">
            <Input value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })}
              placeholder={form.kind === 'cost' ? 'e.g. Emergent, Cloudflare' : 'e.g. Acme Corp'}
              className="h-8 text-[12px]" data-testid="acc-ledger-form-party" />
          </Field>
          <Field label="Category">
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="llm, cdn, freelancer…"
              className="h-8 text-[12px]" data-testid="acc-ledger-form-category" />
          </Field>
          <Field label="Amount (USD)">
            <Input type="number" min={0} step="0.01" value={form.amount_usd}
              onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
              className="h-8 font-mono-stat text-[12px]" data-testid="acc-ledger-form-amount" />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.occurred_on}
              onChange={(e) => setForm({ ...form, occurred_on: e.target.value })}
              className="h-8 text-[12px]" data-testid="acc-ledger-form-date" />
          </Field>
          <Field label="Note (optional)" className="md:col-span-5">
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="h-8 text-[12px]" data-testid="acc-ledger-form-notes" />
          </Field>
          <div className="flex items-end">
            <Button size="sm" onClick={addRow} className="h-8 w-full gap-1.5" data-testid="acc-ledger-form-add">
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>
      </div>

      {/* Rows table */}
      <div className="max-h-96 overflow-auto rounded border border-border">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-panel-header/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left">Date</th>
              <th className="px-2 py-1.5 text-left">Kind</th>
              <th className="px-2 py-1.5 text-left">Party</th>
              <th className="px-2 py-1.5 text-left">Category</th>
              <th className="px-2 py-1.5 text-right">Amount</th>
              <th className="px-2 py-1.5 text-left">Status</th>
              <th className="px-2 py-1.5 text-left">Note</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody data-testid="acc-ledger-tbody">
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-[11px] text-muted-foreground">
                No ledger rows in this window. Add one above.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`acc-ledger-row-${r.id}`}>
                <td className="px-2 py-1 font-mono-stat">{r.occurred_on}</td>
                <td className="px-2 py-1 uppercase">
                  <span className={cn('rounded px-1.5 py-0.5 text-[9.5px] font-bold tracking-wider',
                    r.kind === 'cost' ? 'bg-rose-500/15 text-rose-600' : 'bg-emerald-500/15 text-emerald-600')}>
                    {r.kind === 'cost' ? 'COST' : 'A/R'}
                  </span>
                </td>
                <td className="px-2 py-1 font-medium">{r.party}</td>
                <td className="px-2 py-1 text-muted-foreground">{r.category}</td>
                <td className={cn('px-2 py-1 text-right font-mono-stat',
                  r.kind === 'cost' ? 'text-destructive' : 'text-success')}>
                  {fmtUsd(r.amount_usd)}
                </td>
                <td className="px-2 py-1 capitalize">
                  <span className={cn('rounded px-1.5 py-0.5 text-[9.5px] font-semibold tracking-wider',
                    r.status === 'cleared'   ? 'bg-emerald-500/15 text-emerald-600' :
                    r.status === 'cancelled' ? 'bg-neutral-500/15 text-neutral-500' :
                                                'bg-amber-500/15 text-amber-600')}>
                    {r.status}
                  </span>
                </td>
                <td className="px-2 py-1 text-muted-foreground max-w-[220px] truncate" title={r.notes}>
                  {r.notes || '—'}
                </td>
                <td className="px-2 py-1 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {r.status !== 'cleared' && (
                      <button onClick={() => setStatus(r.id, 'cleared')}
                        data-testid={`acc-ledger-clear-${r.id}`}
                        className="rounded border border-border px-1.5 py-0.5 text-[9.5px] hover:bg-secondary">
                        Clear
                      </button>
                    )}
                    {r.status !== 'cancelled' && (
                      <button onClick={() => setStatus(r.id, 'cancelled')}
                        data-testid={`acc-ledger-cancel-${r.id}`}
                        className="rounded border border-border px-1.5 py-0.5 text-[9.5px] hover:bg-secondary">
                        Cancel
                      </button>
                    )}
                    <button onClick={() => removeRow(r.id)}
                      data-testid={`acc-ledger-delete-${r.id}`}
                      className="text-destructive hover:underline">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────── Aged AR / AP Reports (Sep-2026) ──────────────────
// Aging buckets from journal_service.aged_accounts_{receivable,payable}.
// Provides a drilldown table + a "Chase overdue" bulk-email action.

function AgedReportsSection() {
  const [tab, setTab] = useState('ap');       // 'ap' | 'ar'
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chasing, setChasing] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = tab === 'ap' ? await adminApi.gaapAgedAp()
                              : await adminApi.gaapAgedAr();
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aged report load failed.');
    } finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);

  const chaseOverdue = async () => {
    if (!data?.rows?.length) return;
    const overdue = data.rows.filter(r => r.bucket !== 'current');
    if (!overdue.length) {
      toast.info('No overdue rows to chase.');
      return;
    }
    if (!(await openConfirm({
      title: `Chase ${overdue.length} overdue ${tab === 'ap' ? 'vendor' : 'customer'} row(s)?`,
      description:
        tab === 'ap'
          ? 'A/P: emails the CFO with a signed digest of every overdue vendor payable.'
          : 'A/R: sends a polite dunning email to each customer with an outstanding balance.',
      confirmLabel: 'Send chase',
    }))) return;
    setChasing(true);
    try {
      // Fire the payables digest (reuses the CFO email path). For AR
      // we currently only mark each row — send-email wiring for
      // customer chase is Phase 3.
      if (tab === 'ap') {
        await adminApi.payablesDigestSend();
        toast.success(`Chase digest sent for ${overdue.length} overdue vendor(s).`);
      } else {
        const r = await adminApi.gaapChaseBulk(['1_30','31_60','61_90','over_90']);
        toast.success(`${r.sent}/${r.count} A/R chase email(s) sent (${r.failed} failed).`);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chase failed.');
    } finally { setChasing(false); }
  };

  const bucketLabel = {
    current: 'Current',
    '1_30':  '1–30 d',
    '31_60': '31–60 d',
    '61_90': '61–90 d',
    over_90: '90+ d',
  };
  const bucketTone = {
    current: 'bg-emerald-500/15 text-emerald-600',
    '1_30':  'bg-amber-500/15 text-amber-600',
    '31_60': 'bg-orange-500/15 text-orange-600',
    '61_90': 'bg-rose-500/15 text-rose-600',
    over_90: 'bg-red-600/25 text-red-700 font-bold',
  };
  const buckets = data?.buckets || {};
  const total   = data?.total || 0;
  const rows    = data?.rows || [];

  return (
    <div className="mb-5 rounded-md border border-border bg-card p-4" data-testid="acc-aged">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <Clock className="h-4 w-4" /> Aged Reports
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Bucket every open row by (today − {tab === 'ap' ? 'due_date' : 'occurred_on'}) into
            standard aging windows. Use <b>Chase overdue</b> to notify the CFO with a
            signed digest of everything past due.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5 text-[11px]"
            data-testid="acc-aged-tab">
            {[['ap','A/P'],['ar','A/R']].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                data-testid={`acc-aged-tab-${k}`}
                className={cn('rounded-sm px-2 py-0.5',
                  tab === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}>
                {label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
            className="h-7 gap-1 text-[11px]" data-testid="acc-aged-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
          <Button size="sm" onClick={chaseOverdue}
            disabled={chasing || !rows.some(r => r.bucket !== 'current')}
            className="h-7 gap-1.5 text-[11px]" data-testid="acc-aged-chase">
            {chasing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Chase overdue
          </Button>
        </div>
      </div>

      {/* Bucket totals strip */}
      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-6">
        {['current','1_30','31_60','61_90','over_90'].map((k) => (
          <div key={k}
            className={cn('rounded border border-border bg-background/50 p-2', bucketTone[k])}
            data-testid={`acc-aged-total-${k}`}>
            <div className="text-[9.5px] uppercase tracking-wider opacity-80">{bucketLabel[k]}</div>
            <div className="font-mono-stat text-[13px] font-semibold">{fmtUsd(buckets[k] || 0)}</div>
          </div>
        ))}
        <div className="rounded border border-primary/40 bg-primary/5 p-2"
          data-testid="acc-aged-total-grand">
          <div className="text-[9.5px] uppercase tracking-wider text-primary/80">Total open</div>
          <div className="font-mono-stat text-[13px] font-bold text-primary">{fmtUsd(total)}</div>
        </div>
      </div>

      {/* Rows table */}
      <div className="max-h-96 overflow-auto rounded border border-border">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-panel-header/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left">{tab === 'ap' ? 'Due' : 'Occurred'}</th>
              <th className="px-2 py-1.5 text-left">{tab === 'ap' ? 'Vendor' : 'Customer'}</th>
              <th className="px-2 py-1.5 text-left">Category</th>
              <th className="px-2 py-1.5 text-right">Amount</th>
              <th className="px-2 py-1.5 text-right">Days {tab === 'ap' ? 'overdue' : 'outstanding'}</th>
              <th className="px-2 py-1.5 text-left">Bucket</th>
              <th className="px-2 py-1.5 text-right w-px"></th>
            </tr>
          </thead>
          <tbody data-testid="acc-aged-tbody">
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-[11px] text-muted-foreground">
                {busy ? 'Loading…' : `No open ${tab === 'ap' ? 'payables' : 'receivables'}.`}
              </td></tr>
            ) : rows.map((r, i) => {
              const dateFld = tab === 'ap' ? r.due_date : r.occurred_on;
              const partyFld = tab === 'ap' ? r.vendor : r.party;
              const daysFld  = tab === 'ap' ? r.days_overdue : r.days_outstanding;
              const chaseOne = async () => {
                try {
                  const res = await adminApi.gaapChaseOne(r.id);
                  if (res.ok) toast.success(`Chase sent to ${res.sent_to}`);
                  else toast.error(res.reason || 'Chase failed.');
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Chase failed.');
                }
              };
              return (
                <tr key={r.id || i} className="border-t border-border" data-testid={`acc-aged-row-${i}`}>
                  <td className="px-2 py-1 font-mono-stat">{dateFld}</td>
                  <td className="px-2 py-1 font-medium">{partyFld}</td>
                  <td className="px-2 py-1 text-muted-foreground">{r.category}</td>
                  <td className="px-2 py-1 text-right font-mono-stat">{fmtUsd(r.amount_usd)}</td>
                  <td className={cn('px-2 py-1 text-right font-mono-stat',
                      daysFld > 0 ? 'text-destructive' : 'text-muted-foreground')}>
                    {daysFld > 0 ? `+${daysFld}` : daysFld}
                  </td>
                  <td className="px-2 py-1">
                    <span className={cn('rounded px-1.5 py-0.5 text-[9.5px] font-semibold tracking-wider',
                      bucketTone[r.bucket])}>
                      {bucketLabel[r.bucket]}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    {tab === 'ar' && r.bucket !== 'current' && (
                      <button onClick={chaseOne}
                        data-testid={`acc-aged-chase-one-${r.id}`}
                        className="rounded border border-border px-1.5 py-0.5 text-[9.5px] hover:bg-secondary">
                        Chase
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────── GAAP Reconciliation (Sep-2026) ──────────────────
// Orphan-row detector across the 4 write paths that should be
// double-writing to journal_entries. Bulk "Backfill missing JEs"
// posts the missing entries in one shot using the same routing
// rules as the live write paths.

function ReconciliationSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filling, setFilling] = useState(false);

  const load = async () => {
    setBusy(true);
    try { setData(await adminApi.gaapReconciliation()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Reconciliation load failed.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    if (!(data?.total_missing > 0)) { toast.info('Nothing to backfill.'); return; }
    if (!(await openConfirm({
      title: `Backfill missing journal entries for ${data.total_missing} row(s)?`,
      description:
        'Uses the same GL routing as live writes (resolve_gl_account, ' +
        'post_je_for_cost_event, post_je_for_stripe_fulfilment). ' +
        'Idempotent — safe to re-run.',
      confirmLabel: 'Backfill',
    }))) return;
    setFilling(true);
    try {
      const r = await adminApi.gaapReconciliationBackfill(500);
      const totalPosted =
        (r.ledger_entries?.posted || 0) +
        (r.payables?.posted || 0) +
        (r.cost_events?.posted || 0) +
        (r.payment_transactions?.posted || 0);
      const totalFailed =
        (r.ledger_entries?.failed || 0) +
        (r.payables?.failed || 0) +
        (r.cost_events?.failed || 0) +
        (r.payment_transactions?.failed || 0);
      toast.success(`Backfilled ${totalPosted} JE(s) (${totalFailed} failed). Re-run if any remain.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed.');
    } finally { setFilling(false); }
  };

  const collLabels = {
    ledger_entries:       'Ledger entries',
    payables:             'Payables',
    cost_events:          'Cost events (COGS)',
    payment_transactions: 'Stripe payments',
  };
  const totalMissing = data?.total_missing || 0;
  const hasIssues = totalMissing > 0;

  return (
    <div className="mb-5 rounded-md border border-border bg-card p-4" data-testid="acc-recon">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            {hasIssues ? <AlertTriangle className="h-4 w-4 text-warning" />
                       : <ShieldCheck className="h-4 w-4 text-success" />}
            Reconciliation
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Every domain row (ledger, payables, cost_events, Stripe payments) should carry a
            <code className="mx-1">je_id</code> pointing at a balanced journal entry.
            {' '}Rows without one indicate a silent JE-write failure that would drop from the trial balance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
            className="h-7 gap-1 text-[11px]" data-testid="acc-recon-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
          <Button size="sm" onClick={backfill}
            disabled={filling || !hasIssues}
            className="h-7 gap-1.5 text-[11px]"
            data-testid="acc-recon-backfill">
            {filling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
            Backfill missing JEs
          </Button>
        </div>
      </div>

      {/* Summary banner */}
      <div className={cn('mb-3 rounded border p-3',
          hasIssues ? 'border-warning/40 bg-warning/5' : 'border-success/40 bg-success/5')}
        data-testid="acc-recon-summary">
        <div className="text-[11.5px] font-semibold">
          {hasIssues
            ? `${totalMissing} row(s) missing a journal entry`
            : 'All domain rows have a matching journal entry ✓'}
        </div>
        {data?.generated_at && (
          <div className="mt-0.5 text-[10.5px] text-muted-foreground">
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* Per-collection cards */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {Object.entries(data?.collections || {}).map(([key, c]) => (
          <div key={key}
            className={cn('rounded border border-border bg-background/50 p-2',
              c.count > 0 && 'border-warning/40 bg-warning/5')}
            data-testid={`acc-recon-coll-${key}`}>
            <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">
              {collLabels[key]}
            </div>
            <div className={cn('font-mono-stat text-[16px] font-semibold',
                c.count > 0 ? 'text-warning' : 'text-success')}>
              {c.count}
            </div>
            {c.sample?.length > 0 && (
              <div className="mt-1.5 max-h-24 space-y-0.5 overflow-y-auto text-[10px] text-muted-foreground">
                {c.sample.slice(0, 5).map((s, i) => (
                  <div key={s.id || s.session_id || i} className="truncate font-mono-stat">
                    {(s.id || s.session_id || '').slice(0, 18)} · {s.vendor || s.party || s.provider || s.user_id || '—'}
                    {s.amount_usd != null && ` · ${fmtUsd(s.amount_usd)}`}
                    {s.base_cost_usd != null && ` · ${fmtUsd(s.base_cost_usd)}`}
                    {s.amount_total_cents != null && ` · ${fmtUsd(s.amount_total_cents / 100)}`}
                  </div>
                ))}
                {c.count > c.sample.length && (
                  <div className="text-[9.5px] italic">+{c.count - c.sample.length} more…</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────── Editorial Payouts ───────────────────────────────
function EditorialPayoutsSection() {
  const [payouts,  setPayouts]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await editorialOrdersAdminApi.listPayouts();
      setPayouts(r.payouts || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pending = payouts.filter(p => p.status === 'pending');
  const paid    = payouts.filter(p => p.status !== 'pending');
  const totalPendingEditor   = pending.reduce((s, p) => s + (p.editor_payout_usd || 0), 0);
  const totalPendingPlatform = pending.reduce((s, p) => s + (p.platform_fee || 0), 0);
  const totalPaidEditor      = paid.reduce(   (s, p) => s + (p.editor_payout_usd || 0), 0);
  const totalGross           = payouts.reduce((s, p) => s + (p.gross_usd || 0), 0);

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="editorial-payouts-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[13px] font-semibold flex items-center gap-1.5">
            <Wallet className="h-3.5 w-3.5 text-primary" /> Editorial Service Payouts
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Auto-created when editorial orders are marked complete. 80% editor / 20% platform split.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1" onClick={load}>
            <RefreshCw className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Hide details' : 'Show all'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Summary KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mb-3">
            <div className="rounded-md border bg-background px-3 py-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Pending editor</p>
              <p className="text-[15px] font-bold text-amber-600">{fmtUsd(totalPendingEditor)}</p>
              <p className="text-[10px] text-muted-foreground">{pending.length} order{pending.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="rounded-md border bg-background px-3 py-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Pending platform</p>
              <p className="text-[15px] font-bold text-rose-700">{fmtUsd(totalPendingPlatform)}</p>
              <p className="text-[10px] text-muted-foreground">20% platform cut</p>
            </div>
            <div className="rounded-md border bg-background px-3 py-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Paid out (editor)</p>
              <p className="text-[15px] font-bold text-emerald-600">{fmtUsd(totalPaidEditor)}</p>
              <p className="text-[10px] text-muted-foreground">{paid.length} settled</p>
            </div>
            <div className="rounded-md border bg-background px-3 py-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total gross</p>
              <p className="text-[15px] font-bold">{fmtUsd(totalGross)}</p>
              <p className="text-[10px] text-muted-foreground">{payouts.length} total orders</p>
            </div>
          </div>

          {payouts.length === 0 && (
            <p className="text-[12px] text-muted-foreground py-2">
              No payout records yet — records are created automatically when editorial orders are completed.
            </p>
          )}

          {expanded && payouts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11.5px]">
                <thead>
                  <tr className="border-b text-muted-foreground text-left">
                    <th className="pb-1.5 font-medium">Payout ID</th>
                    <th className="pb-1.5 font-medium">Editor</th>
                    <th className="pb-1.5 font-medium text-right">Gross</th>
                    <th className="pb-1.5 font-medium text-right">Platform</th>
                    <th className="pb-1.5 font-medium text-right text-emerald-700">Editor</th>
                    <th className="pb-1.5 font-medium">Status</th>
                    <th className="pb-1.5 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {payouts.map(p => (
                    <tr key={p.id} data-testid={`ep-row-${p.id}`}>
                      <td className="py-1.5 font-mono text-[10px] text-muted-foreground">{p.id?.slice(-10)}</td>
                      <td className="py-1.5">{p.editor_name || p.editor_id || '—'}</td>
                      <td className="py-1.5 text-right">{fmtUsd(p.gross_usd)}</td>
                      <td className="py-1.5 text-right text-rose-700">{fmtUsd(p.platform_fee)}</td>
                      <td className="py-1.5 text-right font-semibold text-emerald-700">{fmtUsd(p.editor_payout_usd)}</td>
                      <td className="py-1.5">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          p.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                        }`}>{p.status}</span>
                      </td>
                      <td className="py-1.5 text-muted-foreground">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────── Digest Subscribers ──────────────────────────────
function DigestSubscribersSection() {
  const [recipients, setRecipients] = useState([]);
  const [seats, setSeats]           = useState([]);   // all admin credentials
  const [busy, setBusy]             = useState(false);
  const [adding, setAdding]         = useState(false);
  const [email, setEmail]           = useState('');
  const [label, setLabel]           = useState('');
  const [sendBusy, setSendBusy]     = useState(false);
  const [toggling, setToggling]     = useState({});   // username → true while saving
  const [emailEdits, setEmailEdits] = useState({});   // username → pending email string
  const [showSeats, setShowSeats]   = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [recResp, seatsResp] = await Promise.all([
        adminApi.payablesDigestRecipients(),
        adminApi.credentialsFull(),
      ]);
      setRecipients(recResp.recipients || []);
      setSeats(seatsResp.credentials || []);
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  // ── add extra recipient ────────────────────────────────────────────
  const handleAdd = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try {
      await adminApi.payablesDigestAddRecipient({ email: email.trim(), label: label.trim() });
      toast.success(`${email.trim()} added to digest`);
      setEmail(''); setLabel('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add recipient');
    } finally { setAdding(false); }
  };

  const handleRemove = async (id, addr) => {
    if (!(await openConfirm({
      title: `Remove ${addr}?`,
      description: 'They will no longer receive the Crown & Rose · Daily Payables digest.',
      confirmLabel: 'Remove',
    }))) return;
    try {
      await adminApi.payablesDigestRemoveRecipient(id);
      toast.success(`${addr} removed`);
      setRecipients(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  // ── financials scope toggle ────────────────────────────────────────
  const handleToggle = async (seat) => {
    const username    = seat.username;
    const hasFinancials = Array.isArray(seat.scopes) && seat.scopes.includes('financials');
    const newScopes   = hasFinancials
      ? seat.scopes.filter(s => s !== 'financials')
      : [...(seat.scopes || []), 'financials'];

    // If enabling and no email on the seat, save whatever is in emailEdits first
    if (!hasFinancials && !seat.email) {
      const pending = (emailEdits[username] || '').trim();
      if (!pending) {
        toast.warning(`Set an email address for ${username} before enabling the digest.`);
        return;
      }
      try {
        setToggling(t => ({ ...t, [username]: true }));
        await adminApi.updateCredentialEmail(username, pending);
        // Update local seat email
        setSeats(prev => prev.map(s =>
          s.username === username ? { ...s, email: pending } : s,
        ));
        setEmailEdits(e => { const n = { ...e }; delete n[username]; return n; });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to save email');
        setToggling(t => { const n = { ...t }; delete n[username]; return n; });
        return;
      }
    }

    setToggling(t => ({ ...t, [username]: true }));
    try {
      const r = await adminApi.updateCredentialScopes(username, newScopes);
      setSeats(prev => prev.map(s =>
        s.username === username ? { ...s, scopes: r.scopes } : s,
      ));
      toast.success(hasFinancials
        ? `${username} removed from digest`
        : `${username} will now receive the daily digest`);
      // Refresh recipient list so the counts update
      const recResp = await adminApi.payablesDigestRecipients();
      setRecipients(recResp.recipients || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scope update failed');
    } finally {
      setToggling(t => { const n = { ...t }; delete n[username]; return n; });
    }
  };

  // ── send digest now ────────────────────────────────────────────────
  const handleSendNow = async () => {
    setSendBusy(true);
    try {
      const r = await adminApi.payablesDigestSend();
      if (r.skipped) {
        toast.info(`Skipped: ${r.skipped}`);
      } else {
        toast.success(`Digest sent to ${r.delivered?.length ?? r.recipients} recipient(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSendBusy(false); }
  };

  const adminSeats = recipients.filter(r => r.type === 'admin_seat');
  const extras     = recipients.filter(r => r.type === 'extra');

  return (
    <div className="mb-5 rounded-md border border-border bg-card p-4" data-testid="acc-digest-subscribers">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <Mail className="h-4 w-4" /> Digest Subscribers
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Manage who receives the <strong>Crown &amp; Rose · Daily Payables</strong> email each morning.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load} disabled={busy}
            className="flex h-7 w-7 items-center justify-center rounded border border-border text-muted-foreground hover:bg-secondary"
            data-testid="acc-digest-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
          <Button size="sm" onClick={handleSendNow} disabled={sendBusy}
            className="h-7 gap-1.5 text-[11px]" data-testid="acc-digest-send-now">
            {sendBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Send now
          </Button>
        </div>
      </div>

      {/* ── Admin seat toggles ── */}
      <div className="mb-4 rounded border border-border overflow-hidden" data-testid="acc-digest-seats">
        <button
          onClick={() => setShowSeats(s => !s)}
          className="flex w-full items-center justify-between bg-muted/40 px-3 py-2 text-left hover:bg-muted/60"
          data-testid="acc-digest-seats-toggle">
          <div className="flex items-center gap-2 text-[11.5px] font-semibold">
            <Users className="h-3.5 w-3.5" />
            Admin Seats
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9.5px] text-primary">
              {seats.filter(s => Array.isArray(s.scopes) && s.scopes.includes('financials')).length} / {seats.length} enabled
            </span>
          </div>
          <span className="text-[10px] text-muted-foreground">{showSeats ? '▲ hide' : '▼ show'}</span>
        </button>

        {showSeats && (
          <div className="divide-y divide-border/60" data-testid="acc-digest-seats-list">
            {seats.length === 0 && (
              <div className="px-3 py-3 text-center text-[11.5px] text-muted-foreground">No admin seats found.</div>
            )}
            {seats.map(seat => {
              const hasFinancials = Array.isArray(seat.scopes) && seat.scopes.includes('financials');
              const isBusy = !!toggling[seat.username];
              const needsEmail = !hasFinancials && !seat.email;

              return (
                <div key={seat.username}
                  className={cn('flex items-center gap-3 px-3 py-2',
                    hasFinancials ? 'bg-emerald-50/40' : 'bg-background')}
                  data-testid={`acc-digest-seat-${seat.username}`}>

                  {/* Toggle switch */}
                  <button
                    onClick={() => handleToggle(seat)}
                    disabled={isBusy}
                    title={hasFinancials ? 'Revoke digest access' : 'Grant digest access'}
                    data-testid={`acc-digest-seat-toggle-${seat.username}`}
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent transition-colors',
                      hasFinancials ? 'bg-emerald-500' : 'bg-muted-foreground/30',
                      isBusy && 'opacity-50 cursor-not-allowed',
                    )}>
                    {isBusy
                      ? <Loader2 className="mx-auto h-3 w-3 animate-spin text-white" />
                      : <span className={cn(
                          'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
                          hasFinancials ? 'translate-x-4' : 'translate-x-0',
                        )} />
                    }
                  </button>

                  {/* Seat info */}
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-semibold leading-none">{seat.username}</span>
                      {hasFinancials && (
                        <span className="rounded-full border border-emerald-300/60 bg-emerald-50 px-1.5 py-px text-[9.5px] font-semibold text-emerald-700">
                          digest on
                        </span>
                      )}
                      {seat.scopes?.includes('pro_writer') && (
                        <span className="rounded-full border border-primary/20 bg-primary/8 px-1.5 py-px text-[9.5px] text-primary">
                          pro_writer
                        </span>
                      )}
                    </div>
                    {seat.email
                      ? <span className="text-[10.5px] text-muted-foreground">{seat.email}</span>
                      : needsEmail
                        ? (
                          <div className="mt-1 flex items-center gap-1.5">
                            <Input
                              value={emailEdits[seat.username] || ''}
                              onChange={e => setEmailEdits(prev => ({ ...prev, [seat.username]: e.target.value }))}
                              onKeyDown={e => e.key === 'Enter' && handleToggle(seat)}
                              placeholder="Add email to enable digest"
                              className="h-6 max-w-[220px] text-[10.5px] border-amber-300 focus-visible:ring-amber-400"
                              data-testid={`acc-digest-seat-email-${seat.username}`} />
                            <span className="text-[9.5px] text-amber-600">required to enable</span>
                          </div>
                        )
                        : <span className="text-[10.5px] italic text-muted-foreground/60">no email set</span>
                    }
                  </div>

                  {/* Scope chips (compact) */}
                  <div className="hidden shrink-0 items-center gap-1 lg:flex">
                    {(seat.scopes || []).filter(s => s !== 'financials').slice(0, 3).map(s => (
                      <span key={s}
                        className="rounded border border-border/60 bg-muted/40 px-1.5 py-px text-[9px] text-muted-foreground">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Active recipients ── */}
      {recipients.length === 0 && !busy && (
        <div className="mb-3 rounded border border-dashed border-border bg-muted/20 py-5 text-center text-[11.5px] text-muted-foreground"
          data-testid="acc-digest-empty">
          No recipients yet. Toggle an admin seat above or add an extra email below.
        </div>
      )}

      {recipients.length > 0 && (
        <div className="mb-3 space-y-1" data-testid="acc-digest-list">
          {adminSeats.length > 0 && (
            <div className="mb-1 text-[9.5px] uppercase tracking-wider text-muted-foreground">
              Admin seats (financials scope)
            </div>
          )}
          {adminSeats.map(r => (
            <div key={r.id}
              className="flex items-center justify-between rounded border border-border/60 bg-background/50 px-3 py-1.5"
              data-testid={`acc-digest-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <Shield className="h-3.5 w-3.5 text-primary/60" />
                <span className="text-[12px] font-medium">{r.email}</span>
                {r.label && <span className="text-[10.5px] text-muted-foreground">({r.label})</span>}
              </div>
              <span className="rounded-full border border-primary/20 bg-primary/8 px-1.5 py-0.5 text-[9.5px] text-primary">
                admin seat
              </span>
            </div>
          ))}

          {extras.length > 0 && (
            <div className={cn('text-[9.5px] uppercase tracking-wider text-muted-foreground',
              adminSeats.length > 0 && 'mt-2')}>
              Extra recipients
            </div>
          )}
          {extras.map(r => (
            <div key={r.id}
              className="flex items-center justify-between rounded border border-border/60 bg-background/50 px-3 py-1.5"
              data-testid={`acc-digest-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[12px] font-medium">{r.email}</span>
                {r.label && <span className="text-[10.5px] text-muted-foreground">({r.label})</span>}
              </div>
              <button
                onClick={() => handleRemove(r.id, r.email)}
                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Remove"
                data-testid={`acc-digest-remove-${r.id}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Add extra email ── */}
      <div className="flex items-end gap-2 rounded border border-dashed border-border bg-muted/20 p-3"
        data-testid="acc-digest-add-form">
        <div className="flex-1">
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Email</div>
          <Input
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="cfo@yourcompany.com"
            className="h-8 text-[12px]"
            data-testid="acc-digest-email-input" />
        </div>
        <div className="w-36">
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Label (optional)</div>
          <Input
            value={label}
            onChange={e => setLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="CFO"
            className="h-8 text-[12px]"
            data-testid="acc-digest-label-input" />
        </div>
        <Button size="sm" onClick={handleAdd} disabled={adding || !email.trim()}
          className="h-8 gap-1.5 text-[11px] shrink-0" data-testid="acc-digest-add-btn">
          {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
          Add
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────── Legacy export intact
export function _LegacyEnd() { return null; }
